import {
  Alert,
  AlertIcon,
  Box,
  Center,
  Code,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import React from "react";
import { auditTexts } from "../../../../constants/auditTexts";
import type { AuditLog } from "../../../../types";

interface AuditTableProps {
  data: AuditLog[];
  isLoading: boolean;
  error: Error | null;
}

const AuditTable: React.FC<AuditTableProps> = ({ data, isLoading, error }) => {
  if (isLoading) {
    return (
      <Center p={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error.message || auditTexts.messages.error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Center p={8}>
        <Text>{auditTexts.messages.noData}</Text>
      </Center>
    );
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("pt-BR");
  };

  return (
    <Box overflowX="auto">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>{auditTexts.columns.timestamp}</Th>
            <Th>{auditTexts.columns.user}</Th>
            <Th>{auditTexts.columns.table}</Th>
            <Th>{auditTexts.columns.action}</Th>
            <Th>{auditTexts.columns.record}</Th>
            <Th>{auditTexts.columns.changes}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((log) => (
            <Tr key={log.id}>
              <Td>{formatTimestamp(log.timestamp)}</Td>
              <Td>
                {log.user_name} ({log.user_email})
              </Td>
              <Td>
                {auditTexts.tables[
                  log.table_name as keyof typeof auditTexts.tables
                ] || log.table_name}
              </Td>
              <Td>
                {auditTexts.actions[
                  log.action as keyof typeof auditTexts.actions
                ] || log.action}
              </Td>
              <Td>
                <Code>{log.record_id}</Code>
              </Td>
              <Td>
                <Code whiteSpace="pre-wrap" p={2} borderRadius="md" w="full">
                  {JSON.stringify(
                    {
                      old: log.old_values,
                      new: log.new_values,
                      changed: log.changed_fields,
                    },
                    null,
                    2
                  )}
                </Code>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default AuditTable;
